// Larger step components for Used-gun intake, split out to keep the
// orchestrator file under the file-size cap.

import { AcceptStep, PrepRow } from "./components";
import { fmtMoney } from "./fixtures";
import { TouchButton } from "./MockShell";
import { showToast } from "./toast";

export function ValuationStep({
  offerCents,
  setOfferCents,
  suggestedRetailCents,
  targetMarginPct,
  accepted,
  setAccepted,
  onNext,
}: {
  offerCents: number;
  setOfferCents: (n: number) => void;
  suggestedRetailCents: number;
  targetMarginPct: number;
  accepted: boolean;
  setAccepted: (b: boolean) => void;
  onNext: () => void;
}) {
  const suggested = Math.round(suggestedRetailCents * (1 - targetMarginPct / 100));
  const marginPct = Math.round(((suggestedRetailCents - offerCents) / suggestedRetailCents) * 100);
  const needsManager = marginPct < targetMarginPct;
  const bumpBy = (deltaCents: number) =>
    setOfferCents(Math.max(0, Math.min(suggestedRetailCents, offerCents + deltaCents)));

  return (
    <>
      <div className="bg-white rounded-2xl p-5">
        <div className="text-[13px] font-medium text-slate-500 uppercase tracking-wider">
          Your offer
        </div>
        <div className="mt-2 text-[44px] font-semibold tracking-tight tabular-nums text-slate-900 truncate">
          {fmtMoney(offerCents)}
        </div>
        <div className="mt-1 text-[13px] inline-flex items-center gap-1.5">
          <span className={needsManager ? "text-amber-700" : "text-slate-500"}>
            Margin {marginPct}%
          </span>
          {needsManager && (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Needs manager
            </span>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <ChipButton onClick={() => bumpBy(-2500)}>−$25</ChipButton>
          <ChipButton onClick={() => bumpBy(-500)}>−$5</ChipButton>
          <ChipButton onClick={() => setOfferCents(suggested)}>Suggested</ChipButton>
          <ChipButton onClick={() => bumpBy(500)}>+$5</ChipButton>
          <ChipButton onClick={() => bumpBy(2500)}>+$25</ChipButton>
        </div>
      </div>

      <div className="mt-4 bg-white rounded-2xl overflow-hidden">
        <KpiRow
          label="Suggested"
          value={fmtMoney(suggested)}
          sub={`at ${targetMarginPct}% margin`}
        />
        <KpiRow
          label="Expected retail"
          value={fmtMoney(suggestedRetailCents)}
          sub="GunBroker 90-day avg"
        />
      </div>

      <div className="mt-5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-1 pb-2">
        Recent comps
      </div>
      <div className="bg-white rounded-2xl overflow-hidden">
        <CompRow date="Oct 12" source="GunBroker" condition="90% · function OK" cents={67500} />
        <CompRow date="Sep 28" source="Our shop" condition="95% · box + papers" cents={74900} />
        <CompRow date="Sep 02" source="GunBroker" condition="85% · minor wear" cents={62000} />
      </div>

      {!accepted ? (
        <div className="mt-6">
          <TouchButton size="lg" full onClick={() => setAccepted(true)}>
            Accept offer
          </TouchButton>
        </div>
      ) : (
        <>
          <div className="mt-5 bg-white rounded-2xl overflow-hidden">
            <AcceptStep ok label="Seller license scanned" />
            <AcceptStep ok label="Acquired from individual" />
            <AcceptStep ok label="Firearm unit + item created" />
            <AcceptStep ok label="Payout: cash · trade credit · check" />
          </div>
          <div className="mt-6">
            <TouchButton size="lg" full onClick={onNext}>
              Commit
            </TouchButton>
          </div>
        </>
      )}
    </>
  );
}

function ChipButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="min-h-9 px-3.5 rounded-full bg-slate-100 text-[14px] font-medium text-slate-700 active:bg-slate-200 tabular-nums"
    >
      {children}
    </button>
  );
}

function KpiRow({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 min-h-12 hairline last:shadow-none">
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] text-slate-900">{label}</span>
        {sub && <span className="block text-[13px] text-slate-500">{sub}</span>}
      </span>
      <span className="text-[15px] font-medium tabular-nums text-slate-900">{value}</span>
    </div>
  );
}

function CompRow({
  date,
  source,
  condition,
  cents,
}: {
  date: string;
  source: string;
  condition: string;
  cents: number;
}) {
  return (
    <div className="flex items-start gap-3 px-4 py-3 hairline last:shadow-none">
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] text-slate-900">
          {date} · {source}
        </span>
        <span className="block text-[13px] text-slate-500">{condition}</span>
      </span>
      <span className="text-[15px] font-medium tabular-nums text-slate-900 shrink-0">
        {fmtMoney(cents)}
      </span>
    </div>
  );
}

const payoutLabel: Record<string, string> = {
  buyback: "Pay the seller",
  "trade-in": "Issue trade credit",
  evaluate: "Appraisal only",
};

export function ReceiveStep({ intakeType }: { intakeType: string }) {
  const payoutText = payoutLabel[intakeType] ?? "Payout";

  return (
    <>
      <p className="text-[14px] text-slate-500 mb-4 px-1">
        Do these now while the seller is still at the counter.
      </p>

      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-1 pb-2">
        Right now
      </div>
      <div className="bg-white rounded-2xl overflow-hidden">
        <PrepRow label={payoutText} action="Open" />
        <PrepRow label="Print the seller's receipt" action="Print" />
        <PrepRow label="Print serial tag · attach to firearm" action="Print" />
        <PrepRow label="Place in receiving bin · Shelf B-3" action="Confirm" />
      </div>

      <div className="mt-5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-1 pb-2">
        Queue for later
      </div>
      <div className="bg-white rounded-2xl overflow-hidden">
        <PrepRow label="Reconditioning checklist" action="Later" />
        <PrepRow label="Web listing · WooCommerce + GunBroker" action="Later" />
      </div>

      <div className="mt-6">
        <TouchButton
          size="lg"
          full
          onClick={() => {
            showToast("Intake complete");
            window.location.hash = "#/mocks/today";
          }}
        >
          Done
        </TouchButton>
      </div>
    </>
  );
}
